import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api, ApiError } from '@/lib/api';
import { Button, Dot, ErrorBox, Label, Segmented, Spinner, fieldStyles } from '@/components/ui';
import s from './AuthPage.module.css';
import logo from '@/assets/sterling_logo.png';

type Mode = 'login' | 'register';

/** Length-based only — a strength meter that claims more than it knows is worse than none. */
function passwordStrength(password: string): { score: 0 | 1 | 2 | 3; label: string } {
  if (password.length === 0) return { score: 0, label: '' };
  if (password.length < 8) return { score: 1, label: 'weak' };
  if (password.length < 12) return { score: 2, label: 'good' };
  return { score: 3, label: 'strong' };
}

const STRENGTH_COLORS = ['', 'var(--danger)', 'var(--warning)', 'var(--positive)'];

export default function AuthPage() {
  const { login, register } = useAuth();
  const [params] = useSearchParams();
  // The landing page's "Get started" links point here with ?mode=register, so
  // a visitor who chose "create an account" doesn't land on the sign-in tab.
  const [mode, setMode] = useState<Mode>(params.get('mode') === 'register' ? 'register' : 'login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Forgot-password overlay: null = normal auth, 'email' = asking where to
  // send the code, 'code' = code + new password. Success drops back to login.
  const [forgotStage, setForgotStage] = useState<'email' | 'code' | null>(null);
  const [resetCode, setResetCode] = useState('');
  const [notice, setNotice] = useState('');

  const isRegister = mode === 'register';
  const strength = passwordStrength(password);

  async function onForgotSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('That email doesn’t look right.');
      return;
    }

    setBusy(true);
    try {
      if (forgotStage === 'email') {
        const res = await api<{ message: string }>('/auth/forgot', {
          method: 'POST',
          body: { email: email.trim() },
        });
        setNotice(res.message);
        setForgotStage('code');
      } else {
        if (!/^\d{6}$/.test(resetCode)) {
          setError('Enter the 6-digit code from the email.');
          return;
        }
        if (password.length < 8) {
          setError('Password needs at least 8 characters.');
          return;
        }
        const res = await api<{ message: string }>('/auth/reset', {
          method: 'POST',
          body: { email: email.trim(), code: resetCode, password },
        });
        setForgotStage(null);
        setResetCode('');
        setPassword('');
        setNotice(res.message);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t reach the server.');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');

    // Mirror the server's rules so the common mistakes never cost a round trip.
    if (isRegister && !name.trim()) {
      setError('Add your name so the advisor knows who it works for.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('That email doesn’t look right.');
      return;
    }
    if (password.length < 8) {
      setError('Password needs at least 8 characters.');
      return;
    }

    setBusy(true);
    try {
      if (isRegister) await register(name.trim(), email.trim(), password);
      else await login(email.trim(), password);
      // On success the auth context flips and the router swaps in the app.
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Couldn’t reach the server. Is the API running on port 4000?',
      );
      setBusy(false);
    }
  }

  return (
    <div className={s.page}>
      <aside className={s.aside}>
        {/* The only way back to the marketing page once you've landed here. */}
        <Link to="/" className={s.brand} aria-label="Sterling home">
          <img src={logo} alt="" className={s.logo} />
          <div>
            <div className={s.wordmark}>Sterling</div>
            <div className={s.byline}>by cardinale</div>
          </div>
        </Link>

        <div className={s.pitch}>
          <h1 className={s.headline}>Your money, with an advisor built in.</h1>
          <p className={s.sub}>
            Track everything, see the analytics that matter, and let the advisor tell you how much to
            save and where to put it.
          </p>

          <div className={s.preview}>
            <div className={s.previewHead}>
              <Dot lime pulse />
              advisor · this week
            </div>
            <div className={s.previewBody}>
              Found idle cash earning nothing, one annual subscription you’ve stopped using, and room
              to raise your savings rate without touching the buffer.
            </div>
          </div>
        </div>

        <div className={s.footnote}>
          A Cardinale company · your data stays on your own server
        </div>
      </aside>

      <main className={s.main}>
        <form className={s.form} onSubmit={forgotStage ? onForgotSubmit : onSubmit} noValidate>
          {forgotStage ? null : (
            <div className={s.tabs}>
              <Segmented<Mode>
                ariaLabel="Sign in or create an account"
                value={mode}
                onChange={(next) => {
                  setMode(next);
                  setError('');
                  setNotice('');
                }}
                options={[
                  { value: 'login', label: 'Sign in' },
                  { value: 'register', label: 'Create account' },
                ]}
              />
            </div>
          )}

          <div className={s.heading}>
            {forgotStage ? 'Reset your password' : isRegister ? 'Start with Sterling' : 'Welcome back'}
          </div>
          <div className={s.subheading}>
            {forgotStage === 'email'
              ? 'We’ll email you a 6-digit code.'
              : forgotStage === 'code'
                ? 'Enter the code and pick a new password.'
                : isRegister
                  ? 'Free and self-hosted. No card, no third party.'
                  : 'Pick up where you left off.'}
          </div>

          {forgotStage ? (
            <div className={s.fields}>
              <div>
                <Label htmlFor="email">email</Label>
                <input
                  id="email"
                  type="email"
                  className={fieldStyles.input}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  disabled={forgotStage === 'code'}
                />
              </div>

              {forgotStage === 'code' ? (
                <>
                  <div>
                    <Label htmlFor="resetCode">code from the email</Label>
                    <input
                      id="resetCode"
                      className={fieldStyles.input}
                      inputMode="numeric"
                      maxLength={6}
                      value={resetCode}
                      onChange={(e) => {
                        setResetCode(e.target.value.replace(/\D/g, ''));
                        setError('');
                      }}
                      placeholder="123456"
                      autoComplete="one-time-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPassword">new password</Label>
                    <input
                      id="newPassword"
                      type="password"
                      className={fieldStyles.input}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError('');
                      }}
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                  </div>
                </>
              ) : null}

              {error ? <ErrorBox>{error}</ErrorBox> : null}
              {notice && !error ? <div className={s.terms}>{notice}</div> : null}

              <Button type="submit" variant="primary" className={s.submit} disabled={busy}>
                {busy ? <Spinner /> : null}
                {busy ? 'Working…' : forgotStage === 'email' ? 'Send code →' : 'Set new password →'}
              </Button>

              <button
                type="button"
                className={s.linkButton}
                onClick={() => {
                  setForgotStage(forgotStage === 'code' ? 'email' : null);
                  setError('');
                  setNotice('');
                }}
              >
                ← Back
              </button>
            </div>
          ) : (
          <div className={s.fields}>
            {isRegister ? (
              <div className={s.animate}>
                <Label htmlFor="name">full name</Label>
                <input
                  id="name"
                  className={fieldStyles.input}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError('');
                  }}
                  placeholder="Maya Rowan"
                  autoComplete="name"
                />
              </div>
            ) : null}

            <div>
              <Label htmlFor="email">email</Label>
              <input
                id="email"
                type="email"
                className={fieldStyles.input}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>

            <div>
              <div className={s.pwRow}>
                <Label htmlFor="password">password</Label>
                {!isRegister ? (
                  <button
                    type="button"
                    className={s.linkButton}
                    onClick={() => {
                      setForgotStage('email');
                      setError('');
                      setNotice('');
                    }}
                  >
                    Forgot password?
                  </button>
                ) : null}
              </div>
              <input
                id="password"
                type="password"
                className={fieldStyles.input}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="••••••••"
                autoComplete={isRegister ? 'new-password' : 'current-password'}
              />
              {isRegister ? (
                <div className={s.strength}>
                  {[1, 2, 3].map((level) => (
                    <span
                      key={level}
                      className={s.strengthBar}
                      style={
                        strength.score >= level
                          ? { background: STRENGTH_COLORS[strength.score] }
                          : undefined
                      }
                    />
                  ))}
                  <span className={s.strengthLabel}>{strength.label}</span>
                </div>
              ) : null}
            </div>

            {error ? <ErrorBox>{error}</ErrorBox> : null}
            {notice && !error ? <div className={s.terms}>{notice}</div> : null}

            <Button type="submit" variant="primary" className={s.submit} disabled={busy}>
              {busy ? <Spinner /> : null}
              {busy ? 'Working…' : isRegister ? 'Create account →' : 'Sign in →'}
            </Button>

            <div className={s.terms}>
              {isRegister
                ? 'The advisor is read-only. It never moves money — it only tells you what it would do.'
                : 'Your session is protected with a rotating refresh token.'}
            </div>
          </div>
          )}
        </form>
      </main>
    </div>
  );
}
